<script>
import { computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';
import { AiEditingIcon } from '@hugeicons/core-free-icons';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    DropdownMenu,
    HugeIcon,
    Icon,
    InboxChannelGlyph,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    showSendChannelSelector: {
      type: Boolean,
      default: false,
    },
    sendChannelMenuItems: {
      type: Array,
      default: () => [],
    },
    activeSendChannelLabel: {
      type: String,
      default: '',
    },
    activeSendChannelIcon: {
      type: String,
      default: '',
    },
    activeSendChannelInbox: {
      type: Object,
      default: null,
    },
    shellClickCollapsesNote: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'setReplyMode',
    'executeCopilotAction',
    'selectSendChannel',
    'collapseNotePanel',
  ],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };

    const { captainTasksEnabled } = useCaptain();
    const replyEditorMode = useMapGetter('draftMessages/getReplyEditorMode');

    const showSuggestReply = computed(
      () =>
        captainTasksEnabled.value &&
        props.conversationId &&
        replyEditorMode.value === REPLY_EDITOR_MODES.REPLY
    );

    const onSuggestReply = () => {
      emit('executeCopilotAction', 'reply_suggestion', props.editorContent);
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleReplyClick,
      handleNoteClick,
      showSuggestReply,
      onSuggestReply,
      aiEditingIcon: AiEditingIcon,
    };
  },
  data() {
    return {
      showSendChannelDropdown: false,
    };
  },
  computed: {
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
  methods: {
    closeSendChannelDropdown() {
      this.showSendChannelDropdown = false;
    },
    toggleSendChannelDropdown() {
      this.showSendChannelDropdown = !this.showSendChannelDropdown;
    },
    onSendChannelMenuAction(item) {
      this.closeSendChannelDropdown();
      this.$emit('selectSendChannel', item.value);
    },
    onHeaderRowClick(event) {
      if (!this.shellClickCollapsesNote) return;
      if (event.target.closest('[data-note-collapse-ignore]')) return;
      if (event.target.closest('button')) return;
      this.$emit('collapseNotePanel');
    },
  },
};
</script>

<template>
  <div
    class="flex h-11 min-h-11 shrink-0 items-center justify-between gap-2 ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
    @click="onHeaderRowClick"
  >
    <div
      v-if="showSendChannelSelector"
      v-on-click-outside="closeSendChannelDropdown"
      data-note-collapse-ignore
      class="relative flex shrink-0 items-center"
    >
      <NextButton
        ghost
        slate
        sm
        class="max-w-[11rem] min-w-0 !h-6 !min-h-6 !gap-1.5 !px-2"
        :aria-label="$t('CONVERSATION.REPLYBOX.SEND_CHANNEL.ARIA')"
        @click.stop="toggleSendChannelDropdown"
      >
        <template #icon>
          <span
            class="inline-flex size-[18px] shrink-0 items-center justify-center"
          >
            <InboxChannelGlyph
              v-if="
                activeSendChannelInbox?.channel_type ||
                activeSendChannelInbox?.channelType
              "
              :inbox="activeSendChannelInbox"
              size="xs"
            />
            <Icon v-else :icon="activeSendChannelIcon || 'i-ph-chats-circle'" />
          </span>
        </template>
        <template #default>
          <span class="min-w-0 flex-1 truncate ltr:text-left rtl:text-right">{{
            activeSendChannelLabel
          }}</span>
          <Icon
            icon="i-ph-caret-down"
            class="size-4 shrink-0 text-n-slate-10 transition-transform duration-200 ltr:ml-0.5 rtl:mr-0.5"
            :class="{ 'rotate-180': showSendChannelDropdown }"
            aria-hidden="true"
          />
        </template>
      </NextButton>
      <DropdownMenu
        v-if="showSendChannelDropdown && sendChannelMenuItems.length"
        :menu-items="sendChannelMenuItems"
        class="ltr:left-0 rtl:right-0 bottom-auto top-full z-[100] mt-1 max-h-56 min-w-[12rem] max-w-sm overflow-y-auto dark:!outline-n-slate-5"
        @action="onSendChannelMenuAction"
      />
    </div>
    <div
      class="mx-2 my-0 flex min-h-0 min-w-0 flex-1 items-center gap-2 self-stretch"
      :class="{ 'cursor-pointer': shellClickCollapsesNote }"
    >
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex shrink-0 items-center gap-2" data-note-collapse-ignore>
      <button
        v-if="showSuggestReply"
        type="button"
        class="inline-flex h-8 max-w-[11rem] min-w-0 items-center gap-1.5 rounded-lg border-0 bg-transparent px-2 text-sm font-medium text-n-violet-11 outline-1 outline-transparent transition-colors hover:enabled:bg-n-violet-9/10 focus-visible:bg-n-violet-9/10 active:enabled:bg-n-violet-9/15 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled || isEditorDisabled"
        :aria-label="
          $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUGGESTION')
        "
        @click.stop="onSuggestReply"
      >
        <HugeIcon
          :icon="aiEditingIcon"
          :size="16"
          class="shrink-0 text-current"
          aria-hidden="true"
        />
        <span class="min-w-0 truncate">{{
          $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUGGESTION')
        }}</span>
      </button>
    </div>
  </div>
</template>
